/**
 * useFieldFilterState (Unit 5 Phase 2) — per-site filter conditions for the
 * field records screen.
 *
 * THE THIRD persisted per-user UI preference in the field zone, and mirrors
 * useFieldSortState arm for arm. useFieldHiddenFields owns the key
 * (`toolsight-field-fsg-<siteId>`), the schema version, the raw read and the
 * merging write; this hook owns TWO top-level keys inside that object:
 *
 *   { v: 1, hiddenFields: [...], sort: [...],
 *     filter: [ { fieldKey, operator, value }, ... ],
 *     filterLogic: 'AND' | 'OR' }
 *
 * BOTH KEYS GO IN ONE persistPatch CALL. They are one decision — a condition
 * list means nothing without the logic that combines it — and two calls would
 * be two read-modify-write cycles where a failure between them leaves a rep
 * with conditions combined by the wrong operator. One patch, one setItem.
 *
 * SCHEMA_VERSION IS 1 AND STAYS 1. `filter` and `filterLogic` are additive
 * siblings, not a new shape. Every read path gates on the same strict `!==`,
 * so a bump would blank hidden columns, sort AND filter in one breath.
 *
 * SANITIZE ON READ, NOT ONLY ON WRITE — same contract as sanitizeSort. A
 * stored condition is dropped when its field no longer exists in any manifest,
 * or when its operator is not in that field kind's set. Both matter: a
 * removed field would have the evaluator reading undefined off every row, and
 * an operator that no longer applies to the kind (a `>` left on a field that
 * became text) would fall through evalCondition's `default: true` and quietly
 * stop filtering while the UI still showed a condition.
 *
 * NEVER THROWS — inherited whole from the storage layer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { persistPatch, readStored } from './useFieldHiddenFields';
import { operatorsForKey, operatorNeedsValue } from '../data/filterRecords';
import { KIND_BY_KEY } from '../data/fieldValueRules';

const SCHEMA_VERSION = 1;

/**
 * ONE frozen empty array, not a fresh [] per call — same reasoning as
 * useFieldSortState's EMPTY_SORT: identity stability keeps the host's useMemo
 * from re-running, and filterRecords returns its input by reference on an
 * empty condition list.
 */
const EMPTY_FILTER = Object.freeze([]);

/** The two legal values. Anything that is not exactly 'OR' means 'AND'. */
export const FILTER_LOGIC = Object.freeze(['AND', 'OR']);

const DEFAULT_LOGIC = 'AND';

/**
 * Coerce any candidate list into clean conditions.
 *
 * Rebuilt, never passed through: a stored condition carrying extra keys (an
 * older build's `values` array from Bob's multi-select operators, a hand edit)
 * contributes only its three meaningful fields. A valueless operator has its
 * value normalised to '' so a stale value cannot linger behind an `is empty`
 * and reappear if the rep switches the operator back.
 */
function sanitizeFilter(list) {
  if (!Array.isArray(list)) return EMPTY_FILTER;
  const out = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const { fieldKey, operator } = entry;
    if (typeof fieldKey !== 'string' || !KIND_BY_KEY.has(fieldKey)) continue;
    if (typeof operator !== 'string') continue;
    if (!operatorsForKey(fieldKey).includes(operator)) continue;
    const value = operatorNeedsValue(operator) ? String(entry.value ?? '') : '';
    out.push({ fieldKey, operator, value });
  }
  return out.length ? out : EMPTY_FILTER;
}

/** 'OR' only when stored exactly; everything else — absent, junk — is 'AND'. */
function sanitizeLogic(raw) {
  return raw === 'OR' ? 'OR' : DEFAULT_LOGIC;
}

/**
 * The conditions for a site. Empty for missing / malformed / wrong-v —
 * MIRRORS readSort (useFieldSortState.js:110-116) arm for arm, including the
 * strict `!==` on the version.
 */
function readFilter(siteId) {
  const stored = readStored(siteId);
  if (!stored) return EMPTY_FILTER;
  if (stored.v !== SCHEMA_VERSION) return EMPTY_FILTER;
  if (!Array.isArray(stored.filter)) return EMPTY_FILTER;
  return sanitizeFilter(stored.filter);
}

/**
 * The combine logic for a site. Same version gate as readFilter — a
 * wrong-version object yields the default, so the logic can never survive a
 * version its conditions did not.
 */
function readLogic(siteId) {
  const stored = readStored(siteId);
  if (!stored) return DEFAULT_LOGIC;
  if (stored.v !== SCHEMA_VERSION) return DEFAULT_LOGIC;
  return sanitizeLogic(stored.filterLogic);
}

export default function useFieldFilterState(siteId) {
  const [filter, setFilterState] = useState(() => readFilter(siteId));
  const [filterLogic, setLogicState] = useState(() => readLogic(siteId));

  // Re-read when the rep opens a different site. Ref guard identical to
  // useFieldSortState:122-128 and useFieldHiddenFields:116-121.
  const loadedFor = useRef(siteId);
  useEffect(() => {
    if (loadedFor.current === siteId) return;
    loadedFor.current = siteId;
    setFilterState(readFilter(siteId));
    setLogicState(readLogic(siteId));
  }, [siteId]);

  // Both mutators are inert without a site: no state change, no write. A write
  // under a literal "toolsight-field-fsg-null" key would be a leak, not a
  // fallback — the same rule every sibling hook follows.
  //
  // setFilter takes BOTH halves because they commit together. The modal's
  // Apply hands up a draft list and a draft logic; splitting them into two
  // setters would put a render between two writes of one decision.
  const setFilter = useCallback((nextFilter, nextLogic) => {
    if (siteId == null) return;
    const clean = sanitizeFilter(nextFilter);
    const logic = sanitizeLogic(nextLogic);
    setFilterState(clean);
    setLogicState(logic);
    persistPatch(siteId, { filter: clean, filterLogic: logic });
  }, [siteId]);

  // Used by the zero-result surface's "Clear filters", which wipes the
  // COMMITTED filter without opening the modal — the one path in this unit
  // where a clear is a commit rather than a draft edit, because there is no
  // draft in play and no Apply to follow it.
  const clearFilter = useCallback(() => {
    if (siteId == null) return;
    setFilterState(EMPTY_FILTER);
    setLogicState(DEFAULT_LOGIC);
    persistPatch(siteId, { filter: [], filterLogic: DEFAULT_LOGIC });
  }, [siteId]);

  return { filter, filterLogic, setFilter, clearFilter };
}
